/**
 * DependencyManager - Manages dependencies between tasks
 * Handles creation, validation, and management of connections between blocks
 */
class DependencyManager {
  /**
   * Create a DependencyManager
   * @param {TimelineCalendar} timeline - The timeline instance
   */
  constructor(timeline) {
    this.timeline = timeline;
    this.connections = [];
    this.dependencyMap = {};
    this.reverseDependencyMap = {};
    this.connectionChangeListeners = [];
    this.hasNewConnections = false;
  }

  /**
   * Add a connection between two blocks
   * @param {string|number} fromId - Source block ID
   * @param {string|number} toId - Target block ID
   * @param {string|number} id - Optional connection ID
   * @param {number} gap - Gap between blocks
   * @returns {string|number|null} - Connection ID or null if failed
   */
  addConnection(fromId, toId, id = null, gap = null) {
    if (!fromId || !toId || fromId === toId) return null;

    const existingConn = this.connections.find(conn =>
      conn.fromId === fromId && conn.toId === toId
    );

    if (existingConn) return existingConn.id;

    if (this.willCreateCycle(fromId, toId)) {
      this.timeline.showNotification(this.timeline.t('Connection failed: Would create cycle'), 'error');
      return null;
    }

    const fromBlock = this.timeline.dataManager.blocks.find(b => b.id === fromId);
    const toBlock = this.timeline.dataManager.blocks.find(b => b.id === toId);

    let calculatedGap = gap;

    // คำนวณ gap ถ้าไม่ได้ระบุมา
    if (calculatedGap === null && fromBlock && toBlock) {
      calculatedGap = this.timeline.connectionManager.calculateGapBetweenBlocks(fromBlock, toBlock);
    }

    // ใช้ค่าเริ่มต้นถ้ายังคำนวณไม่ได้
    if (calculatedGap === null) {
      calculatedGap = 1;
    }

    // ใช้ ID ที่มีอยู่ หรือสร้าง tempId ใหม่โดยใช้ timestamp ปัจจุบัน
    const tempId = Date.now() + '_' + Math.floor(Math.random() * 1000);
    const newConnection = {
      id: id, // ID จะเป็น null สำหรับการเชื่อมโยงใหม่ และจะถูกอัปเดตหลังจากบันทึกลงฐานข้อมูล
      tempId: tempId, // tempId ใช้สำหรับอ้างอิงก่อนได้รับ ID จากฐานข้อมูล
      fromId,
      toId,
      type: 'default',
      gap: calculatedGap
    };

    this.connections.push(newConnection);
    this.rebuildDependencyMap();
    this.notifyConnectionChange('add', newConnection);
    this.hasNewConnections = true;

    return tempId;
  }

  /**
   * Resets the flag that indicates whether new connections have been established.
   * Sets the hasNewConnections property to false.
   */
  resetNewConnectionsFlag() {
    this.hasNewConnections = false;
  }

  /**
   * Remove a connection by ID
   * @param {string|number} id - Connection ID
   * @returns {boolean} - True if removed successfully
   */
  removeConnection(id) {
    if (!id) return false;

    const removedConnection = this.connections.find(conn => conn.id == id);
    if (!removedConnection) return false;

    this.connections = this.connections.filter(conn => conn.id != id);
    this.rebuildDependencyMap();
    this.notifyConnectionChange('remove', removedConnection);

    return true;
  }

  /**
   * Remove a connection by block IDs
   * @param {string|number} fromId - Source block ID
   * @param {string|number} toId - Target block ID
   * @returns {boolean} - True if removed successfully
   */
  removeConnectionByBlockIds(fromId, toId) {
    const conn = this.connections.find(c => c.fromId === fromId && c.toId === toId);
    if (!conn) return false;

    return this.removeConnection(conn.id);
  }

  /**
   * Remove all connections for a specific block ID
   * @param {string|number} blockId - Block ID to remove connections for
   * @returns {number} - Number of removed connections
   */
  removeConnectionsByBlockId(blockId) {
    if (!blockId) return 0;

    const beforeCount = this.connections.length;

    // เก็บรายการการเชื่อมโยงที่จะถูกลบเพื่อส่งการแจ้งเตือน
    const removedConnections = this.connections.filter(conn =>
      conn.fromId == blockId || conn.toId == blockId
    );

    // เก็บรายการบล็อกที่เชื่อมโยงเพื่อการอ้างอิง
    const connectedBlockIds = new Set();
    removedConnections.forEach(conn => {
      if (conn.fromId != blockId) connectedBlockIds.add(conn.fromId);
      if (conn.toId != blockId) connectedBlockIds.add(conn.toId);
    });

    // ลบการเชื่อมโยงที่เกี่ยวข้องกับบล็อก
    this.connections = this.connections.filter(conn =>
      conn.fromId != blockId && conn.toId != blockId
    );

    const removedCount = beforeCount - this.connections.length;

    if (removedCount > 0) {
      // สร้างแผนผังการพึ่งพาใหม่
      this.rebuildDependencyMap();

      // แจ้งเตือนสำหรับแต่ละการเชื่อมโยงที่ถูกลบ
      removedConnections.forEach(conn => {
        this.notifyConnectionChange('remove', conn);
      });
    }

    return removedCount;
  }

  /**
   * Rebuild dependency maps for quick lookups
   */
  rebuildDependencyMap() {
    this.dependencyMap = {};
    this.reverseDependencyMap = {};
    if (!this.connections || this.connections.length === 0) return;
    for (const conn of this.connections) {
      if (!conn.fromId || !conn.toId) {
        console.warn(this.timeline.t('Found incomplete links:'), conn);
        continue;
      }
      if (!this.dependencyMap[conn.fromId]) {
        this.dependencyMap[conn.fromId] = [];
      }
      if (!this.dependencyMap[conn.fromId].includes(conn.toId)) {
        this.dependencyMap[conn.fromId].push(conn.toId);
      }
      if (!this.reverseDependencyMap[conn.toId]) {
        this.reverseDependencyMap[conn.toId] = [];
      }
      if (!this.reverseDependencyMap[conn.toId].includes(conn.fromId)) {
        this.reverseDependencyMap[conn.toId].push(conn.fromId);
      }
    }
  }

  /**
   * Get all dependent blocks
   * @param {string|number} blockId - Source block ID
   * @returns {Array} - Array of dependent block IDs
   */
  getAllDependents(blockId) {
    if (!blockId) return [];

    let result = [];
    let visited = new Set();

    const traverse = (id) => {
      if (visited.has(id)) return;
      visited.add(id);

      const dependents = this.dependencyMap[id] || [];
      for (const depId of dependents) {
        if (!result.includes(depId)) {
          result.push(depId);
        }
        traverse(depId);
      }
    };

    traverse(blockId);
    return result;
  }

  /**
   * Get all blocks that this block depends on
   * @param {string|number} blockId - Target block ID
   * @returns {Array} - Array of dependency block IDs
   */
  getAllDependencies(blockId) {
    if (!blockId) return [];

    let result = [];
    let visited = new Set();

    const traverse = (id) => {
      if (visited.has(id)) return;
      visited.add(id);

      const dependencies = this.reverseDependencyMap[id] || [];
      for (const depId of dependencies) {
        if (!result.includes(depId)) {
          result.push(depId);
        }
        traverse(depId);
      }
    };

    traverse(blockId);
    return result;
  }

  /**
   * Check if there is a cycle in dependencies
   * @returns {boolean} - True if a cycle is detected
   */
  hasCycle() {
    const visited = new Set();
    const recStack = new Set();

    const checkCycle = (blockId) => {
      if (!visited.has(blockId)) {
        visited.add(blockId);
        recStack.add(blockId);

        const dependents = this.dependencyMap[blockId] || [];
        for (const depId of dependents) {
          if (!visited.has(depId) && checkCycle(depId)) {
            return true;
          } else if (recStack.has(depId)) {
            return true;
          }
        }
      }
      recStack.delete(blockId);
      return false;
    };

    for (const fromId in this.dependencyMap) {
      if (checkCycle(fromId)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if adding a connection would create a cycle
   * @param {string|number} fromId - Source block ID
   * @param {string|number} toId - Target block ID
   * @returns {boolean} - True if connection would create a cycle
   */
  willCreateCycle(fromId, toId) {
    if (fromId === toId) return true;

    const dependentsOfToId = this.getAllDependents(toId);
    return dependentsOfToId.includes(fromId);
  }

  /**
   * Get connections formatted for database storage
   * @returns {Array} - Array of connections in database format
   */
  getConnectionsForDatabase() {
    return this.connections.map(conn => ({
      id: conn.id,
      tempId: conn.id || conn.tempId,
      from_id: conn.fromId,
      to_id: conn.toId,
      type: conn.type || 'default',
      gap: conn.gap || 1
    }));
  }

  /**
   * Import connections from database format
   * @param {Array} dbConnections - Connections in database format
   */
  importConnectionsFromDatabase(dbConnections) {
    if (!Array.isArray(dbConnections)) return;
    this.connections = dbConnections.map(conn => ({
      id: conn.id,
      fromId: conn.from_id,
      toId: conn.to_id,
      type: conn.type || 'default',
      gap: conn.gap || 1
    }));
    this.rebuildDependencyMap();
    this.notifyConnectionChange('import', null);
  }

  /**
   * Add a connection change listener
   * @param {Function} listener - Listener function
   */
  addConnectionChangeListener(listener) {
    if (typeof listener === 'function' && !this.connectionChangeListeners.includes(listener)) {
      this.connectionChangeListeners.push(listener);
    }
  }

  /**
   * Remove a connection change listener
   * @param {Function} listener - Listener function to remove
   */
  removeConnectionChangeListener(listener) {
    this.connectionChangeListeners = this.connectionChangeListeners.filter(l => l !== listener);
  }

  /**
   * Notify connection change listeners
   * @param {string} action - Action type: 'add', 'remove', 'import'
   * @param {Object} connection - Connection object
   */
  notifyConnectionChange(action, connection) {
    this.connectionChangeListeners.forEach(listener => {
      try {
        listener(action, connection, this.connections);
      } catch (error) {
        this.timeline.showNotification(
          this.timeline.t('Error in Connection Change Listener: {message}', {message: error.message}),
          'error'
        );
      }
    });
  }

  /**
   * Detect potential cycles for a block
   * @param {string|number} blockId - Block ID to check
   * @returns {boolean} - True if a potential cycle is detected
   */
  detectPotentialCycles(blockId) {
    const tempDependencyMap = JSON.parse(JSON.stringify(this.dependencyMap));
    const visited = new Set();
    const recStack = new Set();

    const checkCycle = (currentId) => {
      if (!visited.has(currentId)) {
        visited.add(currentId);
        recStack.add(currentId);

        const dependents = tempDependencyMap[currentId] || [];
        for (const depId of dependents) {
          if (!visited.has(depId) && checkCycle(depId)) {
            return true;
          } else if (recStack.has(depId)) {
            return true;
          }
        }
      }
      recStack.delete(currentId);
      return false;
    };

    return checkCycle(blockId);
  }

  /**
   * Get all connections
   * @returns {Array} - Array of all connections
   */
  getAllConnections() {
    return this.connections || [];
  }

  /**
   * Get connections in database format
   * @returns {Array} - Array of connections formatted for database
   */
  getConnectionsForAPI() {
    return this.connections.map(conn => ({
      id: conn.id,
      tempId: conn.id || conn.tempId,
      fromId: conn.fromId,
      toId: conn.toId,
      type: conn.type || 'default',
      gap: conn.gap || 1
    }));
  }
}

window.DependencyManager = DependencyManager;
